// src/hooks/useAppVersionWatch.tsx
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { getCurrentExamDoc } from '@/services/firestoreService';

const CHANNEL =
    window.location.origin === 'https://chusho-app.web.app'
        ? 'production'
        : 'staging';

/* 進行状況付きオーバーレイ */
const UpdateOverlay: React.FC<{ progress: number }> = ({ progress }) => (
    <Box
        sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            zIndex: 13000,
            gap: 3,
            textAlign: 'center',
            p: 3
        }}
    >
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6" component="p">
            新しいバージョンを適用しています…
        </Typography>
        <Typography variant="body2">{progress}%</Typography>
    </Box>
);

/* Firestore 監視 + 自動更新 */
export const useAppVersionWatch = (): void => {
    const currentRef = useRef<string | null>(null);
    const [visible, setVisible] = useState(false);
    const [progress, setProgress] = useState(0);

    /* ダミープログレス */
    useEffect(() => {
        if (!visible) return;
        const id = setInterval(() => {
            setProgress((p) => (p < 95 ? p + 5 : p));
        }, 250);
        return () => clearInterval(id);
    }, [visible]);

    /* Firestore 監視 */
    useEffect(() => {
        const unsub = onSnapshot(
            doc(getCurrentExamDoc('pwa_meta', 'version'), 'channels', CHANNEL),
            (snap) => {
                if (!snap.exists()) return;
                const { current } = snap.data() as { current: string };

                if (currentRef.current === null) {
                    currentRef.current = current; // 初回は保存のみ
                    return;
                }

                if (currentRef.current !== current) {
                    setVisible(true);

                    /* SW を新バージョンに切替 */
                    navigator.serviceWorker?.controller?.postMessage({ type: 'SKIP_WAITING' });

                    const reload = () => window.location.reload();

                    navigator.serviceWorker?.addEventListener('controllerchange', () => {
                        setProgress(100);
                        setTimeout(reload, 400);
                    });

                    /* フォールバック */
                    setTimeout(reload, 7000);
                }
            }
        );
        return () => unsub();
    }, []);

    /* ポータル描画 */
    useEffect(() => {
        if (!visible) return;

        const container = document.createElement('div');
        document.body.appendChild(container);

        const isReact18 = (ReactDOM as any).createRoot;
        let dispose: () => void;

        if (isReact18) {
            const root = (ReactDOM as any).createRoot(container);
            root.render(<UpdateOverlay progress={progress} />);
            dispose = () => root.unmount();
        } else {
            ReactDOM.render(<UpdateOverlay progress={progress} />, container);
            dispose = () => ReactDOM.unmountComponentAtNode(container);
        }

        return () => {
            dispose();
            document.body.removeChild(container);
        };
    }, [visible, progress]);
};
